import { Controller } from '@hotwired/stimulus';
import { loadTableauApi, loadTableauVendor} from './helpers/tableau_api_vendor';

// Define the tableau types if they are not included in @types/tableau
declare global {
  interface Window {
    tableau?: any;
  }
}
export default class extends Controller {
  static values = {
    mode: { type: String, default: 'vendor' },
    url: { type: String, default: '#' },
    hideTabs: { type: Boolean, default: false },
    hideToolbar: { type: Boolean, default: false },
    dataReload: { type: Boolean, default: false },
    hideSpinner: { type: Boolean, default: false },
    loadTimeoutMs: { type: Number, default: 120000 },
    postLoadGraceMs: { type: Number, default: 45000 }
  };

  private loadTimeoutId: number | null = null;
  private postLoadTimeoutId: number | null = null;
  private iframeObserver: MutationObserver | null = null;

  disconnect(): void {
    this.clearLoadDetection();
  }

  initialize(): void {
    switch (this.modeValue) {
      case 'Api':
        loadTableauApi()
          .then(() => this.initializeViz())
          .catch((err: Error) => {
            console.error('Failed to load Tableau API:', err);
            this.showError(err);
          });
        break;

      case 'vendor':
        loadTableauVendor();
        this.initializeViz();
        break;

      default:
        console.error(`Unsupported mode: ${this.modeValue}`);
    }
  }

  private initializeViz(): void {
    this.exportPngBtn = document.getElementById('export__png__btn') as HTMLButtonElement | null;
    this.spinnerWrapper = document.getElementById('viz__spinner__wrapper') as HTMLElement | null;
    this.spinner = document.getElementById('viz__spinner') as HTMLElement | null;
    this.errorContainer = document.getElementById('error__container') as HTMLElement | null;

    if (window.tableau) {
      this.showSpinner();
      this.startLoadDetection();

      const vizOptions: tableau.VizCreateOptions = {
        hideTabs: this.hideTabsValue,
        hideToolbar: this.hideToolbarValue,
        onFirstInteractive: this.onFirstInteractiveHandler.bind(this)
      };

      this.newTableauViz = new window.tableau.Viz(this.element as HTMLElement, this.urlValue, vizOptions);

      if (this.exportPngBtn) {
        this.exportPngBtn.addEventListener('click', this.onExportClickHandler.bind(this));
      }
    } else {
      console.error('Tableau JavaScript API is not available.');
      this.showError(new Error('Tableau JavaScript API is not available.'));
    }
  }

  private onFirstInteractiveHandler(): void {
    this.clearLoadDetection();

    if (this.dataReloadValue && this.newTableauViz) {
      this.showSpinner();

      this.newTableauViz.refreshDataAsync().then(
        () => this.hideSpinner(),
        (err: any) => this.showError(err)
      );
    } else {
      this.hideSpinner();
    }
  }

  private startLoadDetection(): void {
    this.clearLoadDetection();

    this.loadTimeoutId = window.setTimeout(() => {
      this.showError(
        new Error(
          `Tableau viz did not become interactive within ${this.loadTimeoutMsValue}ms ` +
            `(URL: ${this.urlValue}). Tableau Server may be unreachable.`
        )
      );
    }, this.loadTimeoutMsValue);

    this.watchForIframeLoad();
  }

  private watchForIframeLoad(): void {
    const attach = (iframe: HTMLIFrameElement): void => {
      iframe.addEventListener('load', () => this.onIframeLoad(), { once: true });
    };

    const existing = this.element.querySelector('iframe');
    if (existing) {
      attach(existing);
      return;
    }

    this.iframeObserver = new MutationObserver(() => {
      const iframe = this.element.querySelector('iframe');
      if (iframe) {
        attach(iframe);
        this.iframeObserver?.disconnect();
        this.iframeObserver = null;
      }
    });
    this.iframeObserver.observe(this.element, { childList: true, subtree: true });
  }

  private onIframeLoad(): void {
    this.postLoadTimeoutId = window.setTimeout(() => {
      this.showError(
        new Error(
          'Tableau viz iframe loaded but never became interactive within ' +
            `${this.postLoadGraceMsValue}ms. Likely a 404, expired trusted ticket, ` +
            `or missing workbook permissions (URL: ${this.urlValue}).`
        )
      );
    }, this.postLoadGraceMsValue);
  }

  private clearLoadDetection(): void {
    if (this.loadTimeoutId !== null) {
      window.clearTimeout(this.loadTimeoutId);
      this.loadTimeoutId = null;
    }
    if (this.postLoadTimeoutId !== null) {
      window.clearTimeout(this.postLoadTimeoutId);
      this.postLoadTimeoutId = null;
    }
    this.iframeObserver?.disconnect();
    this.iframeObserver = null;
  }

  private onExportClickHandler(event: Event): void {
    event.preventDefault();
    this.newTableauViz?.showExportImageDialog();
  }

  private showSpinner(): void {
    if (!this.hideSpinnerValue) {
      this.spinnerWrapper?.classList.remove('hidden');
      this.spinner?.classList.remove('hidden');
      this.errorContainer?.classList.add('hidden');
    }
  }

  private hideSpinner(): void {
    this.spinnerWrapper?.classList.add('opacity-0');
    setTimeout(() => {
      this.spinnerWrapper?.classList.add('hidden');
    }, 1000);
  }

  private showError(err: any): void {
    console.error('Error loading Tableau Viz:', err);
    this.clearLoadDetection();
    this.spinnerWrapper?.classList.add('hidden');
    this.spinner?.classList.add('hidden');
    this.errorContainer?.classList.remove('hidden');
  }
}
